import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';

import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Sidebar, ElementCatalog } from '../components/Sidebar';
import { Toolbar } from '../components/Toolbar';
import { MobileToolbar, MobileContextBar } from '../components/MobileToolbar';
import { InstallHint } from '../components/InstallHint';
import { MapComponent } from '../components/MapComponent';
import { SaveScenarioModal } from '../components/modals/SaveScenarioModal';
import { LoadScenarioModal } from '../components/modals/LoadScenarioModal';
import { CircleDrawModal } from '../components/modals/CircleDrawModal';
import { ElementEditModal } from '../components/modals/ElementEditModal';
import { ShapeEditModal } from '../components/modals/ShapeEditModal';
import { FuehrungModal } from '../components/modals/FuehrungModal';
import { createDefaultOrganisation, normalizeOrganisation } from '../lib/fuehrungsDiagramm';
import { HYDRANT_MIN_ZOOM } from '../components/HydrantLayer';
import { POI_CATEGORIES, POI_MIN_ZOOM } from '../lib/poiCategories';
import { BASE_MAPS, DEFAULT_BASE_MAP } from '../lib/baseMaps';
import { saveScenario, parseScenarioFile } from '../lib/scenarioStorage';
import { useTheme } from '../context/ThemeContext';
import { useMobileLayout, useIsTouch } from '../hooks/use-mobile';
import { cn } from '../lib/utils';
import { pathLength, distance } from '../lib/geo';
import {
  LINE_TOOLS, KIND_BY_TOOL, ARROW_TYPES, ARROW_TYPE_BY_SYMBOL, HOSE_SYMBOL_ID,
  DEFAULT_ARROW_TYPE, DEFAULT_HOSE_TYPE, isPathTool, drawColorFor
} from '../lib/lineStyles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Statuszeile einer OSM-Ebene (Hydranten, POIs) unten auf der Karte
const LayerStatus = ({ status, name, noun, minZoom, testId }) => (
  <div
    className="self-start pointer-events-auto flex items-center gap-2 rounded-md bg-card/95 backdrop-blur border border-border px-3 py-1.5 text-xs font-body shadow"
    data-testid={testId}
  >
    {status.state === 'zoom' && `${name}: näher heranzoomen (ab Stufe ${minZoom})`}
    {status.state === 'loading' && (status.count > 0
      ? `${status.count} ${noun}, weitere werden geladen…`
      : `${name} werden geladen…`)}
    {status.state === 'ok' && `${status.count} ${noun}${status.truncated ? ' (Ausschnitt verkleinern für alle)' : ''}`}
    {status.state === 'error' && (
      <>
        <span>{name} konnten nicht geladen werden{status.detail ? ` – ${status.detail}` : ''}</span>
        <button
          type="button"
          onClick={status.retry}
          className="font-semibold text-lunefire-red underline underline-offset-2 touch:py-2"
          data-testid={`${testId}-retry`}
        >
          Erneut versuchen
        </button>
      </>
    )}
  </div>
);

const readStored = (key, fallback) => {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
};

const store = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignorieren */ }
};

// Alle Punkt-Elemente sind drehbar, Fahrzeuge und Symbole umbenennbar
const ROTATABLE_TYPES = ['vehicle', 'symbol', 'marker'];
// Linien/Pfeile/Schlauchleitungen, Flächen und Kreise haben einen eigenen Bearbeiten-Dialog
const SHAPE_TYPES = ['polyline', 'polygon', 'circle'];
const EDITABLE_TYPES = ['vehicle', 'symbol', ...SHAPE_TYPES];

// Aufeinanderfolgende gleiche Punkte entfernen (z.B. zweiter Klick eines Doppelklicks)
const withoutDuplicates = (points) =>
  points.filter((point, i) => i === 0 || distance(points[i - 1], point) > 0.5);

// Default center (Germany)
const DEFAULT_CENTER = { lat: 51.1657, lng: 10.4515 };
const DEFAULT_ZOOM = 6;

export const PlanspielPage = () => {
  const { isDark } = useTheme();
  const isMobile = useMobileLayout();
  const isTouch = useIsTouch();
  
  // Map state
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [elements, setElements] = useState([]);
  const [history, setHistory] = useState([]);
  // Wiederholen (Strg+Y): rückgängig gemachte Stände, wird bei jeder neuen Änderung geleert
  const [future, setFuture] = useState([]);
  const [mapBrightness, setMapBrightness] = useState(() => {
    const saved = localStorage.getItem('mapBrightness');
    return saved ? parseInt(saved) : (isDark ? 85 : 100);
  });
  
  // UI state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTool, setActiveTool] = useState('select');
  const [vehicles, setVehicles] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  // Fahrzeug/Symbol, das mit dem nächsten Klick/Tipp auf die Karte gesetzt wird
  const [pendingPlacement, setPendingPlacement] = useState(null);
  
  // Modal state
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [showCircleModal, setShowCircleModal] = useState(false);
  const [pendingCirclePosition, setPendingCirclePosition] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentScenarioId, setCurrentScenarioId] = useState(null);
  const [currentScenarioMeta, setCurrentScenarioMeta] = useState({ name: '', description: '' });

  // Führungsstruktur (Einsatzabschnitte, Funkgruppen) – gehört zum Szenario
  const [organisation, setOrganisation] = useState(createDefaultOrganisation);
  const [showFuehrungModal, setShowFuehrungModal] = useState(false);
  const [editElementId, setEditElementId] = useState(null);
  
  // Drawing state
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [measurePoints, setMeasurePoints] = useState([]);
  // Pfeilart für das Pfeil-Werkzeug (auch über die Einträge „Richtungen“ im Elementkatalog wählbar)
  const [arrowType, setArrowType] = useState(DEFAULT_ARROW_TYPE);
  const measureDistance = useMemo(() => pathLength(measurePoints), [measurePoints]);
  
  // Hydranten-Ebene
  const [showHydrants, setShowHydrants] = useState(() => {
    try { return localStorage.getItem('showHydrants') === '1'; } catch { return false; }
  });
  const [hydrantStatus, setHydrantStatus] = useState(null);

  const toggleHydrants = () => {
    setShowHydrants(prev => {
      try { localStorage.setItem('showHydrants', prev ? '0' : '1'); } catch { /* ignorieren */ }
      return !prev;
    });
  };

  // Kartenstil und eingeblendete POI-Kategorien (im Browser gemerkt)
  const [baseMap, setBaseMap] = useState(() => {
    const stored = readStored('baseMap', DEFAULT_BASE_MAP);
    return BASE_MAPS[stored] ? stored : DEFAULT_BASE_MAP;
  });
  const [poiCategories, setPoiCategories] = useState(() => {
    const stored = readStored('poiCategories', []);
    return Array.isArray(stored) ? stored.filter(key => POI_CATEGORIES.some(c => c.key === key)) : [];
  });
  const [poiStatus, setPoiStatus] = useState(null);

  const changeBaseMap = (key) => {
    setBaseMap(key);
    store('baseMap', key);
  };

  const changePoiCategories = (keys) => {
    setPoiCategories(keys);
    store('poiCategories', keys);
  };

  const togglePoiCategory = (key) => {
    changePoiCategories(poiCategories.includes(key)
      ? poiCategories.filter(k => k !== key)
      : POI_CATEGORIES.map(c => c.key).filter(k => k === key || poiCategories.includes(k)));
  };

  const fileInputRef = useRef(null);
  const jsonInputRef = useRef(null);
  const mapRef = useRef(null);

  // Save brightness to localStorage
  const handleBrightnessChange = (value) => {
    setMapBrightness(value);
    localStorage.setItem('mapBrightness', value.toString());
  };

  // Delete single element
  const deleteElement = useCallback((elementId) => {
    setHistory(prev => [...prev.slice(-19), JSON.stringify(elements)]);
    setFuture([]);
    setElements(prev => prev.filter(el => el.id !== elementId));
    setSelectedElement(null);
    toast.success('Element gelöscht');
  }, [elements]);

  // Handle element click. Doppelklick wird hier erkannt, weil die Auswahl beim ersten
  // Klick das Marker-Element austauscht und Leaflet dann kein dblclick mehr meldet.
  const lastClickRef = useRef({ id: null, time: 0 });
  const handleElementClick = useCallback((element) => {
    if (activeTool !== 'select') return;
    const now = Date.now();
    const last = lastClickRef.current;
    if (last.id === element.id && now - last.time < 400) {
      lastClickRef.current = { id: null, time: 0 };
      setSelectedElement(element.id);
      if (EDITABLE_TYPES.includes(element.type)) setEditElementId(element.id);
      return;
    }
    lastClickRef.current = { id: element.id, time: now };
    setSelectedElement(element.id === selectedElement ? null : element.id);
  }, [activeTool, selectedElement]);

  // Fetch vehicles and symbols
  useEffect(() => {
    fetchSymbols();
    fetchVehicles();
  }, []);

  const getDefaultVehicles = () => [
    // Feuerwehr
    { id: 'default-lf20', name: 'LF 20', type: 'LF', description: 'Löschgruppenfahrzeug 20', color: '#ef4444', is_custom: false },
    { id: 'default-lf10', name: 'LF 10', type: 'LF', description: 'Löschgruppenfahrzeug 10', color: '#ef4444', is_custom: false },
    { id: 'default-hlf20', name: 'HLF 20', type: 'HLF', description: 'Hilfeleistungslöschfahrzeug 20', color: '#ef4444', is_custom: false },
    { id: 'default-tlf4000', name: 'TLF 4000', type: 'TLF', description: 'Tanklöschfahrzeug 4000', color: '#ef4444', is_custom: false },
    { id: 'default-dlk23', name: 'DLK 23/12', type: 'DLK', description: 'Drehleiter mit Korb 23/12', color: '#ef4444', is_custom: false },
    { id: 'default-rw', name: 'RW', type: 'RW', description: 'Rüstwagen', color: '#ef4444', is_custom: false },
    { id: 'default-elw1', name: 'ELW 1', type: 'ELW', description: 'Einsatzleitwagen 1', color: '#ef4444', is_custom: false },
    { id: 'default-elw2', name: 'ELW 2', type: 'ELW', description: 'Einsatzleitwagen 2', color: '#ef4444', is_custom: false },
    { id: 'default-kdow', name: 'KdoW', type: 'KdoW', description: 'Kommandowagen', color: '#ef4444', is_custom: false },
    { id: 'default-mtf', name: 'MTF', type: 'MTF', description: 'Mannschaftstransportfahrzeug', color: '#ef4444', is_custom: false },
    { id: 'default-gwg', name: 'GW-G', type: 'GW', description: 'Gerätewagen Gefahrgut', color: '#f59e0b', is_custom: false },
    // Rettungsdienst
    { id: 'default-rtw', name: 'RTW', type: 'RTW', description: 'Rettungstransportwagen', color: '#3b82f6', is_custom: false },
    { id: 'default-nef', name: 'NEF', type: 'NEF', description: 'Notarzteinsatzfahrzeug', color: '#3b82f6', is_custom: false },
    { id: 'default-naw', name: 'NAW', type: 'NAW', description: 'Notarztwagen', color: '#3b82f6', is_custom: false },
    { id: 'default-ktw', name: 'KTW', type: 'KTW', description: 'Krankentransportwagen', color: '#3b82f6', is_custom: false },
    { id: 'default-rth', name: 'RTH', type: 'RTH', description: 'Rettungshubschrauber', color: '#f59e0b', is_custom: false },
    // Organisatorische Leitung
    { id: 'default-orgl', name: 'OrgL', type: 'OrgL', description: 'Organisatorischer Leiter', color: '#22c55e', is_custom: false },
    { id: 'default-lna', name: 'LNA', type: 'LNA', description: 'Leitender Notarzt', color: '#22c55e', is_custom: false },
    // Polizei
    { id: 'default-pol-fustw', name: 'FuStW', type: 'POL', description: 'Funkstreifenwagen Polizei', color: '#22c55e', is_custom: false },
    { id: 'default-pol-grukw', name: 'GruKW', type: 'POL', description: 'Gruppenkraftwagen Polizei', color: '#22c55e', is_custom: false },
    { id: 'default-pol-befkw', name: 'BefKW', type: 'POL', description: 'Befehlskraftwagen Polizei', color: '#22c55e', is_custom: false },
    // THW
    { id: 'default-thw-gkw', name: 'GKW', type: 'THW', description: 'Gerätekraftwagen THW', color: '#1e40af', is_custom: false },
    { id: 'default-thw-mtwov', name: 'MTW-OV', type: 'THW', description: 'Mannschaftstransportwagen OV', color: '#1e40af', is_custom: false },
  ];

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
      setVehicles(getDefaultVehicles());
    }
  };

  const fetchSymbols = async () => {
    try {
      const response = await axios.get(`${API}/symbols`);
      setSymbols(response.data || []);
    } catch (error) {
      console.error('Failed to fetch symbols:', error);
    }
  };

  // Save current state to history for undo
  const saveToHistory = useCallback(() => {
    setHistory(prev => [...prev.slice(-19), JSON.stringify(elements)]);
    setFuture([]);
  }, [elements]);

  // Add element to map
  const addElement = useCallback((element) => {
    saveToHistory();
    setElements(prev => [...prev, element]);
  }, [saveToHistory]);

  // Update element position after drag
  const handleElementDrag = useCallback((elementId, newPosition) => {
    saveToHistory();
    setElements(prev => prev.map(el => 
      el.id === elementId 
        ? { ...el, position: { lat: newPosition.lat, lng: newPosition.lng } }
        : el
    ));
  }, [saveToHistory]);

  // Rotate selected element (Richtungspfeile)
  const rotateElement = useCallback((elementId, delta) => {
    saveToHistory();
    setElements(prev => prev.map(el =>
      el.id === elementId
        ? { ...el, rotation: (((el.rotation || 0) + delta) % 360 + 360) % 360 }
        : el
    ));
  }, [saveToHistory]);

  const selectedElementData = elements.find(el => el.id === selectedElement);
  const canRotateSelected = ROTATABLE_TYPES.includes(selectedElementData?.type);
  const canEditSelected = EDITABLE_TYPES.includes(selectedElementData?.type);

  // Eigenschaften eines Elements ändern (Bezeichnung, Funkrufname, Abschnitt)
  const updateElementProperties = useCallback((elementId, patch, withHistory = false) => {
    if (withHistory) saveToHistory();
    setElements(prev => prev.map(el =>
      el.id === elementId ? { ...el, properties: { ...el.properties, ...patch } } : el
    ));
  }, [saveToHistory]);

  // Eigenschaften und Geometriewerte (z.B. Kreisradius) eines Elements ändern
  const updateElement = useCallback((elementId, { properties = {}, ...rest }) => {
    saveToHistory();
    setElements(prev => prev.map(el =>
      el.id === elementId ? { ...el, ...rest, properties: { ...el.properties, ...properties } } : el
    ));
  }, [saveToHistory]);

  const placedVehicles = useMemo(() => elements.filter(el => el.type === 'vehicle'), [elements]);
  const editElement = elements.find(el => el.id === editElementId) || null;

  // Tastenkürzel: R = im Uhrzeigersinn, Shift+R = gegen den Uhrzeigersinn
  useEffect(() => {
    if (!canRotateSelected) return undefined;
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        rotateElement(selectedElement, e.shiftKey ? -15 : 15);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canRotateSelected, selectedElement, rotateElement]);

  // Fahrzeug oder Symbol aus dem Katalog an einer Position einfügen
  const placeItem = useCallback((type, item, position) => {
    if (type === 'vehicle') {
      addElement({
        id: uuidv4(),
        type: 'vehicle',
        position,
        properties: {
          vehicleId: item.id,
          name: item.name,
          type: item.type,
          description: item.description,
          color: item.color
        }
      });
      toast.success(`${item.name} hinzugefügt`);
    } else if (type === 'symbol') {
      addElement({
        id: uuidv4(),
        type: 'symbol',
        position,
        properties: {
          symbolId: item.id,
          name: item.name,
          category: item.category,
          icon: item.icon,
          color: item.color
        }
      });
      toast.success(`${item.name} hinzugefügt`);
    }
  }, [addElement]);

  // Handle map click based on active tool
  const handleMapClick = useCallback((latlng, tool) => {
    const point = { lat: latlng.lat, lng: latlng.lng };

    if (pendingPlacement) {
      placeItem(pendingPlacement.type, pendingPlacement.item, point);
      setPendingPlacement(null);
      return;
    }
    
    if (tool === 'select') {
      // Deselect when clicking on map
      setSelectedElement(null);
    } else if (tool === 'circle') {
      setPendingCirclePosition(latlng);
      setShowCircleModal(true);
    } else if (isPathTool(tool)) {
      setDrawingPoints(prev => [...prev, point]);
    } else if (tool === 'measure') {
      setMeasurePoints(prev => [...prev, point]);
    }
  }, [pendingPlacement, placeItem]);

  // Finish drawing (double-click or button)
  const finishDrawing = useCallback(() => {
    const points = withoutDuplicates(drawingPoints);
    if (LINE_TOOLS.includes(activeTool) && points.length >= 2) {
      const kind = KIND_BY_TOOL[activeTool];
      addElement({
        id: uuidv4(),
        type: 'polyline',
        positions: points,
        properties: {
          kind,
          label: '',
          ...(kind === 'arrow' ? { arrowType } : {}),
          ...(kind === 'hose' ? { hoseType: DEFAULT_HOSE_TYPE } : {})
        }
      });
      setDrawingPoints([]);
      toast.success(kind === 'arrow' ? `${ARROW_TYPES[arrowType].label} erstellt`
        : kind === 'hose' ? 'Schlauchleitung erstellt' : 'Linie erstellt');
    } else if (activeTool === 'polygon' && points.length >= 3) {
      addElement({
        id: uuidv4(),
        type: 'polygon',
        positions: points,
        properties: { label: '' }
      });
      setDrawingPoints([]);
      toast.success('Fläche erstellt');
    } else if (activeTool === 'measure') {
      // Clear measurement
      setMeasurePoints([]);
    }
  }, [activeTool, drawingPoints, addElement, arrowType]);

  // Cancel drawing
  const cancelDrawing = useCallback(() => {
    setDrawingPoints([]);
    setMeasurePoints([]);
  }, []);

  // Letzten gesetzten Punkt zurücknehmen (Linie, Fläche oder Messung)
  const removeLastPoint = useCallback(() => {
    if (activeTool === 'measure') setMeasurePoints(prev => prev.slice(0, -1));
    else setDrawingPoints(prev => prev.slice(0, -1));
  }, [activeTool]);

  // Handle tool change
  const handleToolChange = useCallback((tool) => {
    // Finish or cancel current drawing when switching tools
    if (drawingPoints.length > 0) {
      cancelDrawing();
    }
    if (measurePoints.length > 0) {
      setMeasurePoints([]);
    }
    setActiveTool(tool);
    setSelectedElement(null);
    setPendingPlacement(null);
  }, [drawingPoints, measurePoints, cancelDrawing]);

  // Element im Katalog angetippt: nächster Klick/Tipp auf die Karte platziert es,
  // erneutes Antippen hebt die Auswahl wieder auf
  // Pfeile und Schlauchleitung aus dem Katalog werden gezeichnet statt als Punkt gesetzt;
  // liefert das passende Werkzeug oder null
  const startLineFromCatalog = useCallback((item) => {
    if (ARROW_TYPE_BY_SYMBOL[item.id]) {
      setArrowType(ARROW_TYPE_BY_SYMBOL[item.id]);
      handleToolChange('arrow');
      return 'arrow';
    }
    if (item.id === HOSE_SYMBOL_ID) {
      handleToolChange('hose');
      return 'hose';
    }
    return null;
  }, [handleToolChange]);

  const handleCatalogSelect = useCallback((type, item) => {
    if (startLineFromCatalog(item)) return;
    if (pendingPlacement?.item.id === item.id) {
      setPendingPlacement(null);
      return;
    }
    handleToolChange('select');
    setPendingPlacement({ type, item });
  }, [pendingPlacement, handleToolChange, startLineFromCatalog]);

  // Esc bricht das Platzieren ab
  useEffect(() => {
    if (!pendingPlacement) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setPendingPlacement(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pendingPlacement]);

  // Confirm circle creation
  const handleCircleConfirm = useCallback((config) => {
    if (!pendingCirclePosition) return;
    
    addElement({
      id: uuidv4(),
      type: 'circle',
      position: { lat: pendingCirclePosition.lat, lng: pendingCirclePosition.lng },
      radius: config.radius,
      properties: {
        color: config.color,
        label: config.label,
        dashed: config.dashed
      }
    });
    
    setShowCircleModal(false);
    setPendingCirclePosition(null);
  }, [pendingCirclePosition, addElement]);

  // Handle drag start from sidebar
  const handleDragStart = useCallback((e, type, item) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type, item }));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  // Handle drop on map
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;

    const { type, item } = JSON.parse(data);
    
    // Get map container and calculate position
    const mapContainer = e.currentTarget;
    const rect = mapContainer.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Convert pixel coordinates to lat/lng using map reference
    let position = center;
    if (mapRef.current) {
      const map = mapRef.current;
      const point = map.containerPointToLatLng([x, y]);
      position = { lat: point.lat, lng: point.lng };
    }
    
    // Pfeil/Schlauchleitung: Ablagepunkt ist der erste Punkt der Linie
    if (startLineFromCatalog(item)) {
      setDrawingPoints([position]);
      return;
    }
    placeItem(type, item, position);
  }, [center, placeItem, startLineFromCatalog]);

  // Rückgängig: beim Zeichnen/Messen zuerst den letzten Punkt, sonst den letzten Arbeitsschritt
  const undo = useCallback(() => {
    if (drawingPoints.length > 0) {
      setDrawingPoints(prev => prev.slice(0, -1));
      return;
    }
    if (measurePoints.length > 0) {
      setMeasurePoints(prev => prev.slice(0, -1));
      return;
    }
    if (history.length === 0) return;
    setFuture(prev => [...prev.slice(-19), JSON.stringify(elements)]);
    setElements(JSON.parse(history[history.length - 1]));
    setHistory(prev => prev.slice(0, -1));
    setSelectedElement(null);
  }, [drawingPoints, measurePoints, history, elements]);

  const redo = useCallback(() => {
    if (future.length === 0) return;
    setHistory(prev => [...prev.slice(-19), JSON.stringify(elements)]);
    setElements(JSON.parse(future[future.length - 1]));
    setFuture(prev => prev.slice(0, -1));
    setSelectedElement(null);
  }, [future, elements]);

  const canUndo = drawingPoints.length > 0 || measurePoints.length > 0 || history.length > 0;
  const canRedo = future.length > 0;

  // Tastenkürzel: Strg+Z rückgängig, Strg+Y / Strg+Umschalt+Z wiederholen,
  // Enter schließt die Linie/Fläche ab, Esc bricht das Zeichnen ab
  const shortcutsRef = useRef({});
  shortcutsRef.current = { undo, redo, finishDrawing, cancelDrawing, activeTool };
  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      if (document.querySelector('[role="dialog"]')) return;
      const { undo: doUndo, redo: doRedo, finishDrawing: finish, cancelDrawing: cancel, activeTool: tool } = shortcutsRef.current;
      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (mod && key === 'z' && !e.shiftKey) {
        e.preventDefault();
        doUndo();
      } else if (mod && (key === 'y' || (key === 'z' && e.shiftKey))) {
        e.preventDefault();
        doRedo();
      } else if (e.key === 'Enter' && isPathTool(tool)) {
        e.preventDefault();
        finish();
      } else if (e.key === 'Escape' && (isPathTool(tool) || tool === 'measure')) {
        cancel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle toolbar actions
  const handleToolAction = useCallback((action) => {
    switch (action) {
      case 'undo':
        undo();
        break;
      case 'redo':
        redo();
        break;
      case 'clear':
        if (elements.length > 0) {
          saveToHistory();
          setElements([]);
          toast.success('Alle Elemente gelöscht');
        }
        break;
      case 'save':
        setShowSaveModal(true);
        break;
      case 'load':
        setShowLoadModal(true);
        break;
      case 'import-gpx':
        fileInputRef.current?.click();
        break;
      case 'import-json':
        jsonInputRef.current?.click();
        break;
      case 'export':
        exportScenario();
        break;
      default:
        break;
    }
  }, [elements, saveToHistory, undo, redo]);

  // Save scenario (lokal im Browser, gleicher Name aktualisiert das geladene Szenario)
  const handleSaveScenario = ({ name, description }) => {
    setSaving(true);
    try {
      const sameScenario = currentScenarioId && name === currentScenarioMeta.name;
      const saved = saveScenario({
        id: sameScenario ? currentScenarioId : null,
        name,
        description,
        center,
        zoom,
        elements,
        organisation
      });
      setCurrentScenarioId(saved.id);
      setCurrentScenarioMeta({ name: saved.name, description: saved.description });
      toast.success(sameScenario ? 'Szenario aktualisiert' : 'Szenario gespeichert');
      setShowSaveModal(false);
    } catch (error) {
      console.error('Failed to save scenario:', error);
      toast.error('Speichern fehlgeschlagen – Browser-Speicher voll oder gesperrt. Bitte exportieren.');
    } finally {
      setSaving(false);
    }
  };

  // Load scenario
  const handleLoadScenario = (scenario) => {
    saveToHistory();
    setCenter(scenario.center);
    setZoom(scenario.zoom);
    setElements(scenario.elements || []);
    setOrganisation(normalizeOrganisation(scenario.organisation));
    setCurrentScenarioId(scenario.id || null);
    setCurrentScenarioMeta({ name: scenario.name || '', description: scenario.description || '' });
    setSelectedElement(null);
    toast.success(`"${scenario.name}" geladen`);
  };

  // Import scenario from JSON file (Format von "Exportieren")
  const handleJSONImport = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Datei zu groß (max. 5 MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        handleLoadScenario({ ...parseScenarioFile(event.target.result), id: null });
      } catch (error) {
        console.error('JSON import error:', error);
        toast.error('Datei konnte nicht gelesen werden – keine gültige Planspiel-Datei');
      }
    };
    reader.readAsText(file);
  };

  // Export scenario as JSON
  const exportScenario = () => {
    const data = {
      name: currentScenarioMeta.name || 'Planspiel Export',
      description: currentScenarioMeta.description,
      exportedAt: new Date().toISOString(),
      center,
      zoom,
      elements,
      organisation
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `planspiel_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Export erstellt');
  };

  // Handle GPX import
  const handleGPXImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parser = new DOMParser();
        const gpx = parser.parseFromString(event.target.result, 'text/xml');
        
        // Extract waypoints
        const waypoints = gpx.querySelectorAll('wpt');
        if (waypoints.length > 0) {
          const firstWpt = waypoints[0];
          const lat = parseFloat(firstWpt.getAttribute('lat'));
          const lon = parseFloat(firstWpt.getAttribute('lon'));
          setCenter({ lat, lng: lon });
          setZoom(15);
          toast.success(`${waypoints.length} Wegpunkte importiert`);
        }

        // Extract tracks
        const trackPoints = gpx.querySelectorAll('trkpt');
        if (trackPoints.length > 0) {
          const positions = Array.from(trackPoints).map(pt => ({
            lat: parseFloat(pt.getAttribute('lat')),
            lng: parseFloat(pt.getAttribute('lon'))
          }));
          
          if (positions.length >= 2) {
            addElement({
              id: uuidv4(),
              type: 'polyline',
              positions,
              properties: {
                color: '#3b82f6',
                label: 'GPX Track'
              }
            });
          }
          
          // Center on first point
          setCenter(positions[0]);
          setZoom(15);
        }
      } catch (error) {
        console.error('GPX parse error:', error);
        toast.error('Fehler beim Lesen der GPX-Datei');
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };

  // Handle location select from search
  const handleLocationSelect = (location) => {
    setCenter({ lat: location.lat, lng: location.lng });
    setZoom(17);
    toast.success(`Navigiert zu: ${location.name.split(',')[0]}`);
  };

  const isMapDrawing = activeTool !== 'select' || Boolean(pendingPlacement);

  return (
    <div className="h-screen h-dvh w-screen overflow-hidden flex flex-col bg-background" data-testid="planspiel-page">
      <Header 
        onLocationSelect={handleLocationSelect} 
        mapBrightness={mapBrightness}
        onBrightnessChange={handleBrightnessChange}
        baseMap={baseMap}
        onBaseMapChange={changeBaseMap}
        compact={isMobile}
      />
      
      {!isMobile && <Toolbar
        activeTool={activeTool}
        onToolChange={handleToolChange}
        onAction={handleToolAction}
        canUndo={canUndo}
        canRedo={canRedo}
        arrowType={arrowType}
        onArrowTypeChange={setArrowType}
        hasElements={elements.length > 0}
        isDrawing={drawingPoints.length > 0}
        drawingPointsCount={drawingPoints.length}
        measurePointsCount={measurePoints.length}
        measureDistance={measureDistance}
        onFinishDrawing={finishDrawing}
        onCancelDrawing={cancelDrawing}
        onRemoveLastPoint={removeLastPoint}
        selectedElement={selectedElement}
        onDeleteSelected={() => selectedElement && deleteElement(selectedElement)}
        canRotateSelected={canRotateSelected}
        onRotateSelected={(delta) => selectedElement && rotateElement(selectedElement, delta)}
        showHydrants={showHydrants}
        onToggleHydrants={toggleHydrants}
        poiCategories={poiCategories}
        onTogglePoiCategory={togglePoiCategory}
        onSetPoiCategories={changePoiCategories}
        canEditSelected={canEditSelected}
        onEditSelected={() => selectedElement && setEditElementId(selectedElement)}
        onOpenFuehrung={() => setShowFuehrungModal(true)}
      />}

      <div className="flex-1 flex overflow-hidden relative">
        {!isMobile && <Sidebar
          vehicles={vehicles}
          symbols={symbols}
          onDragStart={handleDragStart}
          onSelect={handleCatalogSelect}
          activeId={pendingPlacement?.item.id}
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />}

        {/* Map Container */}
        <div 
          className={`flex-1 relative isolate${isMapDrawing ? ' map-drawing' : ''}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          data-testid="map-container"
        >
          <MapComponent
            center={center}
            zoom={zoom}
            elements={elements}
            onMapClick={handleMapClick}
            onElementClick={handleElementClick}
            onElementDrag={handleElementDrag}
            activeTool={activeTool}
            onMapReady={(map) => { mapRef.current = map; }}
            mapBrightness={mapBrightness}
            isDarkMode={isDark}
            drawingPoints={drawingPoints}
            measurePoints={measurePoints}
            selectedElement={selectedElement}
            showHydrants={showHydrants}
            onHydrantStatus={setHydrantStatus}
            baseMap={baseMap}
            poiCategories={poiCategories}
            onPoiStatus={setPoiStatus}
            onFinishDrawing={finishDrawing}
            isTouch={isTouch}
            suppressDoubleClickZoom={isMapDrawing}
            drawColor={drawColorFor(activeTool, arrowType)}
          />

          {/* Hinweise und Kontextleiste unten auf der Karte (Handy: volle Breite über der Quellenangabe) */}
          <div
            className={cn(
              "absolute z-[1000] flex flex-col gap-2 pointer-events-none",
              isMobile
                ? "bottom-6 left-[max(0.5rem,env(safe-area-inset-left))] right-[max(0.5rem,env(safe-area-inset-right))] sm:mx-auto sm:max-w-xl items-stretch"
                : "bottom-4 left-4 items-start"
            )}
          >
            <InstallHint />

            {showHydrants && hydrantStatus && (
              <LayerStatus
                status={hydrantStatus}
                name="Hydranten"
                noun="Hydranten/Löschwasserstellen"
                minZoom={HYDRANT_MIN_ZOOM}
                testId="hydrant-status"
              />
            )}

            {poiCategories.length > 0 && poiStatus && (
              <LayerStatus status={poiStatus} name="POIs" noun="POIs" minZoom={POI_MIN_ZOOM} testId="poi-status" />
            )}

            {pendingPlacement && (
              <div
                className="pointer-events-auto flex items-center gap-2 rounded-lg border border-lunefire-red/40 bg-card/95 backdrop-blur pl-3 pr-1 py-1 shadow-lg font-body"
                data-testid="placement-hint"
              >
                <span className="flex-1 text-sm">
                  <strong>{pendingPlacement.item.name}</strong>
                  {isTouch ? ': auf die Karte tippen' : ': auf die Karte klicken (Esc bricht ab)'}
                </span>
                <Button size="sm" variant="ghost" className="h-10" onClick={() => setPendingPlacement(null)}>
                  <X className="w-4 h-4 mr-1" /> Abbrechen
                </Button>
              </div>
            )}

            {isMobile && !pendingPlacement && (
              <MobileContextBar
                activeTool={activeTool}
                drawingPointsCount={drawingPoints.length}
                measurePointsCount={measurePoints.length}
                measureDistance={measureDistance}
                onFinishDrawing={finishDrawing}
                onCancelDrawing={cancelDrawing}
                onRemoveLastPoint={removeLastPoint}
                isTouch={isTouch}
                arrowType={arrowType}
                onArrowTypeChange={setArrowType}
                selectedElement={selectedElement}
                onDeleteSelected={() => selectedElement && deleteElement(selectedElement)}
                canRotateSelected={canRotateSelected}
                onRotateSelected={(delta) => selectedElement && rotateElement(selectedElement, delta)}
                canEditSelected={canEditSelected}
                onEditSelected={() => selectedElement && setEditElementId(selectedElement)}
              />
            )}
          </div>
        </div>
      </div>

      {isMobile && (
        <MobileToolbar
          activeTool={activeTool}
          onToolChange={handleToolChange}
          onAction={handleToolAction}
          canUndo={canUndo}
          hasElements={elements.length > 0}
          showHydrants={showHydrants}
          onToggleHydrants={toggleHydrants}
          poiCategories={poiCategories}
          onTogglePoiCategory={togglePoiCategory}
          onSetPoiCategories={changePoiCategories}
          onOpenFuehrung={() => setShowFuehrungModal(true)}
          placingActive={Boolean(pendingPlacement)}
          renderCatalog={(closePanel) => (
            <ElementCatalog
              vehicles={vehicles}
              symbols={symbols}
              onDragStart={handleDragStart}
              onSelect={(type, item) => {
                handleCatalogSelect(type, item);
                closePanel();
              }}
              activeId={pendingPlacement?.item.id}
            />
          )}
        />
      )}

      {/* Hidden file input for GPX import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".gpx"
        onChange={handleGPXImport}
        className="hidden"
        data-testid="gpx-file-input"
      />

      {/* Hidden file input for scenario import */}
      <input
        ref={jsonInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleJSONImport}
        className="hidden"
        data-testid="json-file-input"
      />

      {/* Modals */}
      <SaveScenarioModal
        open={showSaveModal}
        onOpenChange={setShowSaveModal}
        onSave={handleSaveScenario}
        initialName={currentScenarioMeta.name}
        initialDescription={currentScenarioMeta.description}
        loading={saving}
      />

      <LoadScenarioModal
        open={showLoadModal}
        onOpenChange={setShowLoadModal}
        onLoad={handleLoadScenario}
      />

      <ElementEditModal
        open={Boolean(editElement) && !SHAPE_TYPES.includes(editElement.type)}
        onOpenChange={(open) => { if (!open) setEditElementId(null); }}
        element={editElement}
        abschnitte={organisation.abschnitte}
        onSave={(patch) => editElement && updateElementProperties(editElement.id, patch, true)}
      />

      <ShapeEditModal
        open={Boolean(editElement) && SHAPE_TYPES.includes(editElement.type)}
        onOpenChange={(open) => { if (!open) setEditElementId(null); }}
        element={editElement}
        onSave={(patch) => editElement && updateElement(editElement.id, patch)}
      />

      <FuehrungModal
        open={showFuehrungModal}
        onOpenChange={setShowFuehrungModal}
        organisation={organisation}
        onOrganisationChange={setOrganisation}
        vehicles={placedVehicles}
        onUpdateVehicle={updateElementProperties}
        scenarioName={currentScenarioMeta.name}
      />

      <CircleDrawModal
        open={showCircleModal}
        onOpenChange={setShowCircleModal}
        onConfirm={handleCircleConfirm}
        position={pendingCirclePosition}
      />
    </div>
  );
};

export default PlanspielPage;
